import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tab, Nav, Row, Col } from 'react-bootstrap';
import { useUser } from '../UserContext';
import SupporterMenu from './SupporterMenu';

const SettingsModal = ({ show, onClose }) => {
  const { settings: globalSettings, setSettings: setGlobalSettings } = useUser();
  const [localSettings, setLocalSettings] = useState(globalSettings);

  useEffect(() => {
    if (show) {
      setLocalSettings(globalSettings);
    }
  }, [show, globalSettings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let parsedValue = value;
    if (type === 'checkbox') parsedValue = checked;
    if (type === 'range') parsedValue = parseFloat(value);

    setLocalSettings(prev => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const handleSave = () => {
    localStorage.setItem('darkMode', localSettings.darkMode);
    localStorage.setItem('creatorBetaOptIn', localSettings.creatorBetaOptIn);
    localStorage.setItem('popoutPreview', localSettings.popoutPreview);
    localStorage.setItem('artPanDampening', localSettings.artPanDampening);
    localStorage.setItem('cardsPerRow', localSettings.cardsPerRow);
    localStorage.setItem('language', localSettings.language);
    // Persist the specific card title style preference
    localStorage.setItem('cardTitleStyle', localSettings.cardTitleStyle || 'game-like');
    localStorage.setItem('customAutobold', localSettings.customAutobold || '');

    document.cookie = `language_creator=${encodeURIComponent(localSettings.language)}; path=/; max-age=31536000; SameSite=Strict`;
    document.cookie = `language_creator=${encodeURIComponent(localSettings.language)}; path=/gallery; max-age=31536000; SameSite=Strict`;

    setGlobalSettings(localSettings);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Settings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tab.Container defaultActiveKey="creator">
          <Row className="g-3">
            
            {/* LEFT SIDE: Responsive Nav */}
            <Col xs={12} md={3} className="border-md-end pb-3 pb-md-0 border-bottom border-md-bottom-0">
              <Nav 
                variant="pills" 
                className="flex-row flex-md-column justify-content-start justify-content-md-start gap-1"
              >
                <Nav.Item>
                  <Nav.Link eventKey="creator" className="px-3 py-2 text-center text-md-start">Creator</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="gallery" className="px-3 py-2 text-center text-md-start">Gallery</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="patreon" className="px-3 py-2 text-center text-md-start">Patreon</Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>

            {/* RIGHT SIDE: Content */}
            <Col xs={12} md={9} className="pt-2 pt-md-0">
              <Tab.Content style={{ minHeight: '380px' }}>
                
{/* --- CREATOR TAB --- */}
<Tab.Pane eventKey="creator">
  {/* <h5>Creator Settings</h5>
  <hr /> */}

  {/* Card Title Style Selector */}
  <Form.Group controlId="cardTitleStyle" className="mb-4">
    <Form.Label>Card Title Style:</Form.Label>
    <Form.Control
      as="select"
      name="cardTitleStyle"
      value={localSettings.cardTitleStyle || 'game-like'}
      className="form-select"
      onChange={handleChange}
    >
      <option value="game-like">Game Like</option>
      <option value="promotional">Promotional</option>
    </Form.Control>
    <Form.Text className="text-muted d-block mt-2">
      <strong>Game Like:</strong> Simulates how the card title looks in the game.<br />
      <strong>Promotional:</strong> Displays how the card title looks on promotional materials (this is how HearthCards rendered it before).
    </Form.Text>
  </Form.Group>

  {/* Popout Preview Switch with Explanation */}
  <Form.Group controlId="popoutPreview" className="mb-3">
    <Form.Label>Popout Preview (Mobile):</Form.Label>
    <Form.Check
      type="switch"
      name="popoutPreview"
      checked={localSettings.popoutPreview}
      onChange={handleChange}
      label={localSettings.popoutPreview ? "Yes" : "No"}
    />
    <Form.Text className="text-muted d-block mt-1">
      On mobile, scrolling past the main card preview sticks a small version to the top right. It can be moved and zoomed so you can reference it while editing.
    </Form.Text>
  </Form.Group>

  {/* Sensitivity Slider */}
  <Form.Group controlId="artPanDampening" className="mb-3 mt-4">
    <Form.Label>
      Art Pan Sensitivity: <strong>{localSettings.artPanDampening}</strong>
    </Form.Label>
    <Form.Range
      name="artPanDampening"
      min={0.1}
      max={1.0}
      step={0.1}
      value={localSettings.artPanDampening}
      onChange={handleChange}
    />
    <Form.Text className="text-muted d-block mt-1">
      Adjust how fast the art moves relative to your movement. (Default is 0.6. Lower is slower and allows for more precise positioning, 1.0 is direct 1:1 speed).
    </Form.Text>
  </Form.Group>

  <Form.Group controlId="customAutobold" className="mb-3 mt-4">
    <Form.Label>Custom auto-bold keywords:</Form.Label>
    <Form.Control
      as="textarea"
      name="customAutobold"
      rows={3}
      value={localSettings.customAutobold || ''}
      placeholder="e.g. Jackpot, Spell Damage +*, Overload (*)"
      onChange={handleChange}
    />
    <Form.Text className="text-muted d-block mt-1">
      Use * as a wildcard for variable values, for example &quot;Spell Damage +*&quot; or &quot;Overload (*)&quot;. Each variant that needs to be bolded should be entered (ex. Overload (*), Overload, Overloaded).
    </Form.Text>
  </Form.Group>
</Tab.Pane>

                {/* --- GALLERY TAB --- */}
                <Tab.Pane eventKey="gallery">
                  {/* <h5>Gallery Settings</h5>
                  <hr /> */}
                  <Form.Group controlId="cardsPerRow" className="mb-3">
                    <Form.Label>Cards per row (Mobile):</Form.Label>
                    <Form.Control
                      as="select"
                      name="cardsPerRow"
                      value={localSettings.cardsPerRow}
                      className="form-select"
                      onChange={handleChange}
                    >
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                    </Form.Control>
                  </Form.Group>
                  
                  <Form.Group controlId="language" className="mb-3">
                    <Form.Label>Language:</Form.Label>
                    <Form.Control
                      as="select"
                      name="language"
                      value={localSettings.language}
                      className="form-select"
                      onChange={handleChange}
                    >
                      <option value={'en'}>English</option>
                      <option value={'ru'}>Русский</option>
                    </Form.Control>
                  </Form.Group>
                </Tab.Pane>

                {/* --- PATREON TAB --- */}
                <Tab.Pane eventKey="patreon">
                  {/* <h5>Supporter Perks</h5>
                  <hr /> */}
                  <Button href="https://www.hearthcards.net/patreon-php/log_in_user_via_patreon.php" target="_blank" rel="noopener noreferrer">
                    Log in via Patreon
                  </Button>
                  <SupporterMenu />
                </Tab.Pane>

              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SettingsModal;
